import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { useEditMode } from '../../context/EditModeContext';
import { acquireModalLock } from '../../utils/modalLock';
import styles from './ImageCarousel.module.css';

const getImageUrl = (img) => (typeof img === 'string' ? img : img?.url);
const getImageAlt = (img, i, alt) =>
  typeof img === 'string' ? `${alt} ${i + 1}` : img?.alt || `${alt} ${i + 1}`;

function ImageCarousel({ images = [], alt = 'Project screenshot' }) {
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { editMode } = useEditMode();
  const slideRefs = useRef([]);
  const wasLightboxOpen = useRef(false);

  // Filter out empty images (those with no URL or empty string URLs)
  const validImages = useMemo(
    () =>
      images.filter((img) => {
        const imageUrl = getImageUrl(img);
        return imageUrl && imageUrl.trim();
      }),
    [images]
  );

  const count = validImages.length;
  // Derived clamp - the render body must never call setState.
  const safeCurrent = count ? Math.min(current, count - 1) : 0;

  const prev = useCallback(() => {
    setCurrent((c) => {
      const cur = Math.min(c, count - 1);
      return cur <= 0 ? count - 1 : cur - 1;
    });
  }, [count]);

  const next = useCallback(() => {
    setCurrent((c) => {
      const cur = Math.min(c, count - 1);
      return cur >= count - 1 ? 0 : cur + 1;
    });
  }, [count]);

  const openLightbox = useCallback(
    (index) => {
      // While the page is being edited a fullscreen overlay would cover the
      // editor, so selecting the block is the only thing that should happen.
      if (editMode !== 'off') return;
      setCurrent(index);
      setLightboxOpen(true);
    },
    [editMode]
  );

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  // Return focus to the carousel once the overlay is gone.
  useEffect(() => {
    if (lightboxOpen) {
      wasLightboxOpen.current = true;
      return;
    }
    if (!wasLightboxOpen.current) return;
    wasLightboxOpen.current = false;
    const trigger = slideRefs.current[safeCurrent];
    if (trigger) trigger.focus({ preventScroll: true });
  }, [lightboxOpen, safeCurrent]);

  if (!count) {
    return (
      <div className={styles.placeholder}>
        <span className={styles.placeholderText}>Screenshots Coming Soon</span>
      </div>
    );
  }

  // Calculate which dots to show (max 5, with current in middle if possible)
  const maxVisibleDots = 5;
  const middleIndex = 2;
  let startDot = Math.max(0, safeCurrent - middleIndex);
  if (startDot + maxVisibleDots > count) {
    startDot = Math.max(0, count - maxVisibleDots);
  }
  const visibleDots = Array.from({ length: Math.min(maxVisibleDots, count) }, (_, i) => startDot + i);

  return (
    <>
      <div className={styles.carousel}>
        <div
          className={styles.track}
          style={{ transform: `translateX(-${safeCurrent * 100}%)` }}
        >
          {validImages.map((img, i) => (
            <button
              key={i}
              type="button"
              ref={(el) => {
                slideRefs.current[i] = el;
              }}
              className={styles.slideButton}
              onClick={() => openLightbox(i)}
              aria-label={`${getImageAlt(img, i, alt)} - view full screen`}
              // Only the visible slide takes part in the tab order.
              tabIndex={i === safeCurrent ? 0 : -1}
              aria-hidden={i === safeCurrent ? undefined : true}
            >
              <img
                src={getImageUrl(img)}
                alt={getImageAlt(img, i, alt)}
                className={styles.slide}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </button>
          ))}
        </div>

        {count > 1 && (
          <>
            <button className={`${styles.arrow} ${styles.arrowLeft}`} onClick={prev} aria-label="Previous">
              <FaChevronLeft />
            </button>
            <button className={`${styles.arrow} ${styles.arrowRight}`} onClick={next} aria-label="Next">
              <FaChevronRight />
            </button>
            <div className={styles.dots}>
              {visibleDots.map((i) => (
                <button
                  key={i}
                  className={`${styles.dot} ${i === safeCurrent ? styles.dotActive : ''}`}
                  onClick={() => setCurrent(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === safeCurrent ? 'true' : undefined}
                />
              ))}
            </div>
            <div className={styles.counter}>{safeCurrent + 1} / {count}</div>
          </>
        )}
      </div>

      {lightboxOpen && (
        <Lightbox
          images={validImages}
          current={safeCurrent}
          prev={prev}
          next={next}
          onClose={closeLightbox}
          alt={alt}
        />
      )}
    </>
  );
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function Lightbox({ images, current, prev, next, onClose, alt }) {
  const overlayRef = useRef(null);
  const closeRef = useRef(null);

  const trapFocus = useCallback((e) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const focusables = Array.from(overlay.querySelectorAll(FOCUSABLE));
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const inside = overlay.contains(active);

    if (e.shiftKey) {
      if (!inside || active === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (!inside || active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft') {
        prev();
        return;
      }
      if (e.key === 'ArrowRight') {
        next();
        return;
      }
      if (e.key === 'Tab') trapFocus(e);
    },
    [onClose, prev, next, trapFocus]
  );

  // Mount only: lock the page, flag the open modal for the global key handler
  // in App.js, and move focus into the dialog.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const release = acquireModalLock();
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.body.style.overflow = '';
      release();
    };
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      ref={overlayRef}
      className={styles.lightbox}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} - full screen image viewer`}
    >
      <button ref={closeRef} className={styles.lightboxClose} onClick={onClose} aria-label="Close">
        <FaTimes />
      </button>

      <img
        src={getImageUrl(images[current])}
        alt={getImageAlt(images[current], current, alt)}
        className={styles.lightboxImage}
        loading="lazy"
        decoding="async"
      />

      {images.length > 1 && (
        <>
          <button
            className={`${styles.lightboxArrow} ${styles.lightboxArrowLeft}`}
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>
          <button
            className={`${styles.lightboxArrow} ${styles.lightboxArrowRight}`}
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
          >
            <FaChevronRight />
          </button>
          <div className={styles.lightboxCounter}>
            {current + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
}

export default ImageCarousel;
